/**
 * Weight & Price Calculation Helper for Pansar & Herbal Products
 * 
 * Rules specified by user:
 * - When weight is less than 100g (< 100g): +20% markup
 * - 100g to 250g: +15% markup
 * - 250g to 500g: +10% markup
 * - 500g to 750g: +5% markup
 * - 1000g (1 Kg) or higher: Proportional rate (0% extra markup)
 */

export const getMarkupPercentage = (grams) => {
  const g = Number(grams);
  if (g < 100) return 20;
  if (g <= 250) return 15;
  if (g <= 500) return 10;
  if (g <= 750) return 5;
  return 0;
};

export const calculatePriceForGrams = (grams, kgPrice) => {
  const g = Number(grams);
  const p = Number(kgPrice);
  if (!p || p <= 0 || !g || g <= 0) return 0;
  
  const baseCost = (g / 1000) * p;
  const markupPercent = getMarkupPercentage(g);
  const finalPrice = baseCost * (1 + markupPercent / 100);
  
  // Round to nearest whole rupee for clean retail pricing
  return Math.round(finalPrice);
};

export const STANDARD_HERB_WEIGHTS = [
  { grams: 1, weightUrdu: '1 گرام', weightEn: '1g' },
  { grams: 10, weightUrdu: '10 گرام', weightEn: '10g' },
  { grams: 50, weightUrdu: '50 گرام', weightEn: '50g' },
  { grams: 100, weightUrdu: '100 گرام', weightEn: '100g' },
  { grams: 250, weightUrdu: '250 گرام (ایک پاؤ)', weightEn: '250g' },
  { grams: 500, weightUrdu: '500 گرام (آدھا کلو)', weightEn: '500g' },
  { grams: 1000, weightUrdu: '1 کلو گرام (1 Kg)', weightEn: '1 Kg' }
];

export const generateStandardVariants = (kgPrice) => {
  const numericKgPrice = Number(kgPrice) || 0;
  return STANDARD_HERB_WEIGHTS.map(item => ({
    weight: item.weightUrdu,
    weightEn: item.weightEn,
    grams: item.grams,
    price: calculatePriceForGrams(item.grams, numericKgPrice)
  }));
};

export const generateFourStandardVariants = generateStandardVariants;

export const parseWeightToGrams = (weightStr, fallbackPrice = 0) => {
  if (!weightStr) return fallbackPrice || 0;
  const str = String(weightStr).toLowerCase();

  // Explicit keyword checks for Urdu / English idioms
  if (str.includes('تین پاؤ')) return 750;
  if (str.includes('ڈیڑھ کلو')) return 1500;
  if (str.includes('آدھا کلو')) return 500;
  if (str.includes('ایک پاؤ') || (str.includes('پاؤ') && !str.includes('تین'))) return 250;
  
  if (str.includes('تولہ') || str.includes('tola')) {
    const m = str.match(/([0-9.]+)/);
    const count = m ? parseFloat(m[1]) : 1;
    return count * 11.66;
  }

  // Check for Kg / کلو
  if (str.includes('کلو') || str.includes('kg')) {
    const m = str.match(/([0-9.]+)/);
    const count = m ? parseFloat(m[1]) : 1;
    return count * 1000;
  }

  // Check for Litre / لٹر (excluding milli litre / ملی لیٹر)
  if ((str.includes('لٹر') || str.includes('litre')) && !str.includes('ملی') && !str.includes('ml')) {
    const m = str.match(/([0-9.]+)/);
    const count = m ? parseFloat(m[1]) : 1;
    return count * 1000;
  }

  // Check for explicit grams or ml or general digits
  // e.g. "50 گرام", "750g", "100 ml", "60 ملی لیٹر", "50 گولیاں"
  const match = str.match(/([0-9.]+)/);
  if (match) {
    return parseFloat(match[1]);
  }

  return fallbackPrice || 999999;
};

export const sortVariantsByWeight = (variants) => {
  if (!Array.isArray(variants)) return [];
  return [...variants].sort((a, b) => {
    // Check if numeric grams property exists on object
    const gA = a.grams !== undefined && a.grams !== null
      ? Number(a.grams)
      : parseWeightToGrams(a.weight || a.weightEn, a.price);

    const gB = b.grams !== undefined && b.grams !== null
      ? Number(b.grams)
      : parseWeightToGrams(b.weight || b.weightEn, b.price);

    if (gA !== gB) {
      return gA - gB;
    }
    return (Number(a.price) || 0) - (Number(b.price) || 0);
  });
};

